import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { BACKEND_URL } from '../../utils/config';

const TEAL = '#00b5ad';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return '';
  }
};

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16
};
const cardStyle = {
  background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420,
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: 'inherit'
};
const cancelBtnStyle = {
  width: '100%', padding: 11, background: 'transparent', color: '#ef4444',
  border: '1px solid #fca5a5', borderRadius: 9999, fontSize: 14, fontWeight: 600, cursor: 'pointer'
};
const keepBtnStyle = {
  flex: 1, padding: 11, background: TEAL, color: '#fff', border: 'none',
  borderRadius: 9999, fontSize: 13, fontWeight: 600, cursor: 'pointer'
};
const confirmBtnStyle = {
  flex: 1, padding: 11, background: '#fff', color: '#ef4444', border: '1px solid #ef4444',
  borderRadius: 9999, fontSize: 13, fontWeight: 600, cursor: 'pointer'
};

/**
 * Dedicated "Manage subscription" popup with a real two-step cancel confirmation.
 * Opened from the email link (?manage=subscription) and from the credits modal.
 */
const ManageSubscriptionModal = ({ isOpen, onClose, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/credit-packages/subscription-status`, { headers: authHeaders() });
      if (res.ok) setStatus(await res.json());
    } catch (e) {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setConfirmCancel(false);
      setMsg(null);
      load();
    }
  }, [isOpen, load]);

  const doCancel = useCallback(async () => {
    setCancelling(true);
    setMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/credit-packages/cancel-subscription`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setMsg("Your subscription is set to cancel. You'll keep your credits until the end of the current period.");
        setConfirmCancel(false);
        load();
        if (onChanged) onChanged();
      } else {
        setMsg(data.message || 'Could not cancel. Please try again.');
      }
    } catch (e) {
      setMsg('Could not cancel. Please try again.');
    } finally {
      setCancelling(false);
    }
  }, [load, onChanged]);

  if (!isOpen) return null;

  const active = !!(status && status.active);
  const planName = status && status.plan
    ? status.plan.charAt(0).toUpperCase() + status.plan.slice(1)
    : 'Creator';

  return ReactDOM.createPortal(
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>Manage subscription</span>
          <span onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1 }}>×</span>
        </div>

        {loading && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Loading…</div>
        )}

        {!loading && !active && (
          <div style={{ color: '#6b7280', fontSize: 14, padding: '8px 0' }}>
            You don&apos;t have an active subscription.
          </div>
        )}

        {!loading && active && (
          <>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, color: '#111' }}>{planName} · Monthly</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: TEAL, background: '#e6f7f6', padding: '2px 10px', borderRadius: 9999 }}>Active</span>
              </div>
              {status.cancelAtPeriodEnd
                ? <div style={{ fontSize: 13, color: '#b45309' }}>Cancels on {fmtDate(status.renewsAt)}. You keep your credits until then.</div>
                : <div style={{ fontSize: 13, color: '#6b7280' }}>Renews on {fmtDate(status.renewsAt)} · 120 credits / month</div>}
            </div>

            {!status.cancelAtPeriodEnd && !confirmCancel && (
              <button onClick={() => setConfirmCancel(true)} style={cancelBtnStyle}>Cancel subscription</button>
            )}

            {confirmCancel && (
              <div style={{ border: '1px solid #fca5a5', borderRadius: 12, padding: 16, background: '#fef2f2' }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: '#991b1b' }}>Cancel your {planName} plan?</div>
                <div style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 14, lineHeight: 1.5 }}>
                  You&apos;ll keep your credits until {fmtDate(status.renewsAt)}, then you won&apos;t be charged again and your plan returns to free.
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setConfirmCancel(false)} style={keepBtnStyle}>Keep subscription</button>
                  <button onClick={doCancel} disabled={cancelling} style={confirmBtnStyle}>
                    {cancelling ? 'Cancelling…' : 'Yes, cancel'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {msg && (
          <div style={{ marginTop: 14, fontSize: 13, color: '#0f766e', background: '#e6f7f6', padding: '8px 12px', borderRadius: 8 }}>{msg}</div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ManageSubscriptionModal;
